// src/components/MealSuggestion.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useApp } from "../App";
import {
  getFoodEntriesByDate,
  getSavedMeals,
  addFoodEntry,
} from "../services/firebase";
import { calculateDailyTotals } from "../utils/dailyTotals";
import { suggestMeals } from "../utils/mealPlanGenerator";

const today = () => new Date().toISOString().slice(0, 10);

const MEAL_TYPE_BY_HOUR = () => {
  const h = new Date().getHours();
  if (h < 11) return "breakfast";
  if (h < 15) return "lunch";
  return "dinner";
};

export default function MealSuggestion() {
  const { user, profile } = useApp();

  const [suggestions,  setSuggestions]  = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [totals,       setTotals]       = useState({ calories: 0, protein: 0 });
  const [logging,      setLogging]      = useState(null);  // meal id being logged
  const [loggedIds,    setLoggedIds]    = useState([]);

  const loadTotals = useCallback(async () => {
    if (!user) return;
    const { data: foods } = await getFoodEntriesByDate(user.uid, today());
    setTotals(calculateDailyTotals(foods || []));
  }, [user]);

  useEffect(() => { loadTotals(); }, [loadTotals]);

  const handleSuggest = async () => {
    if (!profile) return;
    setLoading(true);

    const { data: saved } = await getSavedMeals(user.uid);
    const remainingCal     = Math.max(0, profile.calorie_target - totals.calories);
    const remainingProtein = Math.max(0, profile.protein_target - totals.protein);

    const results = suggestMeals({
      remaining_calories: remainingCal,
      remaining_protein:  remainingProtein,
      customMeals:        saved || [],
    });

    setSuggestions(results);
    setLoading(false);
  };

  const handleLog = async (meal) => {
    setLogging(meal.id);
    await addFoodEntry(user.uid, {
      date:      today(),
      meal_name: meal.name,
      meal_type: MEAL_TYPE_BY_HOUR(),
      calories:  meal.calories,
      protein:   meal.protein,
      carbs:     meal.carbs,
      fat:       meal.fat,
    });
    setLoggedIds((prev) => [...prev, meal.id]);
    await loadTotals();
    setLogging(null);
  };

  const remaining = {
    calories: Math.max(0, (profile?.calorie_target || 0) - totals.calories),
    protein:  Math.max(0, (profile?.protein_target  || 0) - totals.protein),
  };

  const mealTimeLabel = () => {
    const h = new Date().getHours();
    if (h < 11) return "breakfast";
    if (h < 15) return "lunch";
    if (h < 17) return "dinner or snack";
    return "dinner";
  };

  return (
    <div className="space-y-5 animate-slide-up">
      <div>
        <h1 className="section-title">Meal Suggestions</h1>
        <p className="text-slate-500 text-sm mt-0.5">
          Smart picks based on your remaining budget
        </p>
      </div>

      {/* ── Remaining budget card ─────────────────────────────────────── */}
      <div className="card border-[#ff7c0a]/20">
        <div className="text-xs text-slate-500 uppercase tracking-wider mb-3">Today's Remaining Budget</div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-3xl font-bold text-slate-100"
                 style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              {remaining.calories}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">calories left</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-[#ff7c0a]"
                 style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              {remaining.protein}g
            </div>
            <div className="text-xs text-slate-500 mt-0.5">protein left</div>
          </div>
        </div>
        {remaining.calories < 100 && (
          <div className="mt-3 text-xs text-yellow-400 bg-yellow-900/20 rounded-lg px-3 py-2">
            ⚠️ You're close to your calorie limit. Consider a light snack only.
          </div>
        )}
      </div>

      {/* ── Suggest button ────────────────────────────────────────────── */}
      <button
        onClick={handleSuggest}
        disabled={loading}
        className="btn-primary w-full flex items-center justify-center gap-2 py-3"
      >
        {loading ? (
          <>
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Finding meals…
          </>
        ) : (
          <>
            <SparkIcon className="w-5 h-5" />
            Suggest a {mealTimeLabel()}
          </>
        )}
      </button>

      {/* ── Suggestion context ────────────────────────────────────────── */}
      {suggestions.length > 0 && (
        <div className="text-xs text-slate-500 -mt-2 text-center">
          You have <span className="text-slate-300 font-medium">{remaining.calories} cal</span> and{" "}
          <span className="text-[#ff7c0a] font-medium">{remaining.protein}g protein</span> left — here are 3 suggestions:
        </div>
      )}

      {/* ── Suggestion cards ─────────────────────────────────────────── */}
      <div className="space-y-4">
        {suggestions.length === 0 && !loading && (
          <div className="card text-center py-12 text-slate-500">
            <span className="text-4xl block mb-3">💡</span>
            <p className="text-sm">Hit the button above to get meal suggestions.</p>
            <p className="text-xs mt-1 text-slate-600">
              We'll match meals to your remaining calorie and protein budget.
            </p>
          </div>
        )}

        {suggestions.map((meal, idx) => (
          <SuggestionCard
            key={`${meal.id}_${idx}`}
            meal={meal}
            isLogged={loggedIds.includes(meal.id)}
            isLogging={logging === meal.id}
            remaining={remaining}
            onLog={handleLog}
          />
        ))}
      </div>

      {/* ── No matches message ────────────────────────────────────────── */}
      {suggestions.length === 0 && remaining.calories < 50 && !loading && (
        <div className="card border-emerald-500/20 text-center py-6">
          <span className="text-2xl block mb-2">🎯</span>
          <div className="text-sm font-semibold text-emerald-400">You've hit your calorie target!</div>
          <div className="text-xs text-slate-500 mt-1">
            Great job staying on track. Drink some water and call it a day.
          </div>
        </div>
      )}
    </div>
  );
}

function SuggestionCard({ meal, isLogged, isLogging, remaining, onLog }) {
  const [open, setOpen] = useState(false);

  const fitsCalories = meal.calories <= remaining.calories + 100;
  const calColor     = fitsCalories ? "text-slate-300" : "text-yellow-400";

  return (
    <div className={`card transition-all ${isLogged ? "opacity-60" : "hover:border-[#ff7c0a]/30"}`}>
      {/* Header row */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-100">{meal.name}</span>
            {isLogged && <span className="badge-green text-[10px]">✓ Logged</span>}
          </div>
          <div className={`text-xs mt-1 ${calColor}`}>
            {meal.calories} cal ·{" "}
            <span className="text-[#ff7c0a] font-semibold">{meal.protein}g protein</span> ·{" "}
            {meal.carbs}g carbs · {meal.fat}g fat
          </div>
          {meal.prep_time_minutes > 0 && (
            <div className="text-xs text-slate-500 mt-0.5">
              ⏱ {meal.prep_time_minutes} min prep
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 flex-shrink-0">
          <button
            onClick={() => onLog(meal)}
            disabled={isLogging || isLogged}
            className={`text-xs py-1.5 px-3 rounded-lg font-semibold transition-all ${
              isLogged
                ? "bg-emerald-900/30 text-emerald-400 cursor-default"
                : "btn-primary"
            }`}
          >
            {isLogging ? (
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" />
                Logging
              </span>
            ) : isLogged ? "✓ Logged" : "+ Log it"}
          </button>
        </div>
      </div>

      {/* Tags */}
      {meal.tags && meal.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {meal.tags.map((tag) => (
            <span key={tag} className={
              tag === "quick"        ? "badge-green"  :
              tag === "batch-cook"   ? "badge-blue"   :
              tag === "high-protein" ? "badge-orange" :
              "badge-yellow"
            }>
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Expand recipe */}
      {(meal.ingredients?.length > 0 || meal.instructions?.length > 0) && (
        <>
          <button
            onClick={() => setOpen((o) => !o)}
            className="mt-3 text-xs text-slate-500 hover:text-[#ff7c0a] transition-colors flex items-center gap-1"
          >
            <ChevronIcon className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
            {open ? "Hide recipe" : "Show recipe"}
          </button>

          {open && (
            <div className="mt-3 pt-3 border-t border-[#1f2a3c] space-y-3 animate-fade-in">
              {meal.ingredients?.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                    Ingredients
                  </div>
                  <ul className="space-y-1">
                    {meal.ingredients.map((ing, i) => (
                      <li key={i} className="flex justify-between text-xs">
                        <span className="text-slate-300">{ing.name}</span>
                        <span className="text-slate-500 ml-3 text-right">{ing.quantity}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {meal.instructions?.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                    Instructions
                  </div>
                  <ol className="space-y-1.5">
                    {meal.instructions.map((step, i) => (
                      <li key={i} className="flex gap-2 text-xs text-slate-400">
                        <span className="text-[#ff7c0a] font-semibold flex-shrink-0 w-4">{i + 1}.</span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SparkIcon({ className }) {
  return <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
  </svg>;
}
function ChevronIcon({ className }) {
  return <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
  </svg>;
}
